import React from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment, useMutation } from 'react-relay';
import { type RecordSourceSelectorProxy, ConnectionHandler } from 'relay-runtime';
import StandardButton from '@atlaskit/button/standard-button';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { token } from '@atlaskit/tokens';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { removeApprover_softwareReleasesApprovers_jiraVersion$key } from '@atlassian/jira-relay/src/__generated__/removeApprover_softwareReleasesApprovers_jiraVersion.graphql';
import type { removeApprover_softwareReleasesApprovers_jiraVersionApprover$key } from '@atlassian/jira-relay/src/__generated__/removeApprover_softwareReleasesApprovers_jiraVersionApprover.graphql';
import type {
	removeApprover_softwareReleasesApproversMutation,
	removeApprover_softwareReleasesApproversMutation$data as mutationResponseData,
} from '@atlassian/jira-relay/src/__generated__/removeApprover_softwareReleasesApproversMutation.graphql';
import {
	useRemoveVersionApproversExperienceStart,
	useRemoveVersionApproversExperienceFail,
	useRemoveVersionApproversExperienceSuccess,
} from '../../../../services/remove-approver-experience-tracker';
import messages from './messages';

interface Props {
	jiraVersionApprover: removeApprover_softwareReleasesApprovers_jiraVersionApprover$key;
	jiraVersion: removeApprover_softwareReleasesApprovers_jiraVersion$key;
}

export const RemoveApprover = ({ jiraVersionApprover, jiraVersion }: Props) => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagsService();
	const { id: approverAri, __id: approverStoreId } = useFragment(
		graphql`
			fragment removeApprover_softwareReleasesApprovers_jiraVersionApprover on JiraVersionApprover {
				id
				__id
			}
		`,
		jiraVersionApprover,
	);

	const version = useFragment(
		graphql`
			fragment removeApprover_softwareReleasesApprovers_jiraVersion on JiraVersion {
				__id
			}
		`,
		jiraVersion,
	);
	const connectionId = ConnectionHandler.getConnectionID(version.__id, 'version_approvers');

	const experienceStart = useRemoveVersionApproversExperienceStart();
	const experienceSuccess = useRemoveVersionApproversExperienceSuccess();
	const experienceFail = useRemoveVersionApproversExperienceFail();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'button',
	});

	const [removeApprover] = useMutation<removeApprover_softwareReleasesApproversMutation>(graphql`
		mutation removeApprover_softwareReleasesApproversMutation($approverAri: ID!)
		@raw_response_type {
			jira {
				deleteJiraVersionApprover(id: $approverAri) @optIn(to: "JiraDeleteVersionApprover") {
					success
					deletedApproverId
					errors {
						message
					}
				}
			}
		}
	`);

	const onError = () => {
		const error = Error('Unable to remove approver');
		experienceFail('graphql error', error);
		fireErrorAnalytics({
			meta: {
				id: 'jira.releases.version-detail.approver.remove-approver',
				packageName: 'jiraSoftwareReleasesApprovers',
				teamName: 'fusion-solaris',
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
		});
		showFlag({
			title: messages.removeApproverFailed,
			type: 'error',
		});
	};

	const onDeleteNode = (store: RecordSourceSelectorProxy) => {
		const connectionRecord = store.get(connectionId);
		if (connectionRecord == null) return;

		ConnectionHandler.deleteNode(connectionRecord, approverStoreId);
	};

	return (
		<Wrapper>
			<StandardButton
				appearance="subtle"
				shouldFitContainer
				onClick={() => {
					fireUIAnalytics(analyticsEvent, 'removeApproverFromVersion');
					experienceStart();
					removeApprover({
						variables: {
							approverAri,
						},
						onCompleted: (data: mutationResponseData) => {
							if (!data.jira?.deleteJiraVersionApprover?.success) {
								onError();
								return;
							}
							experienceSuccess();
						},
						onError,
						optimisticUpdater: onDeleteNode,
						updater: onDeleteNode,
					});
				}}
				iconBefore={<CrossIcon size="small" label={formatMessage(messages.removeApprover)} />}
			>
				<Inline>{formatMessage(messages.removeApprover)}</Inline>
			</StandardButton>
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	margin: `${token('space.100', '8px')} 0`,
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Inline = styled.div({
	textAlign: 'left',
});
